/* eslint-disable @typescript-eslint/no-non-null-assertion */
/* eslint-disable @typescript-eslint/no-unsafe-call */
import {
  Box,
  styled,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  TextField,
  Button,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { useEnv } from 'src/context/EnvProvider';
import useLoadDataList from 'src/hook/useLoadDataList';
import { getAllAssemblies } from 'src/services/assemblies';
import { getGranits } from 'src/services/granits';
import { addStock, getPiecesInAssembly, PieceInAssembly } from 'src/services/stock';
import { handleMutation } from 'src/utils/api.utils';

export const AddAssemblyStock = () => {
  const { data: assemblies } = useLoadDataList(getAllAssemblies);
  const { data: granits } = useLoadDataList(getGranits);
  const { buckets } = useEnv();

  const [selectedAssembly, setSelectedAssembly] = useState<string>('');

  const [selectedPieces, setSelectedPieces] = useState<PieceInAssembly[]>([]);

  const {
    data: stockPieces,
    onRefresh,
    refreshing: isLoadingStockPieces,
  } = useLoadDataList(
    () => getPiecesInAssembly(Number.parseInt(selectedAssembly, 10)),
    [selectedAssembly],
    !!selectedAssembly,
  );

  const [quantity, setQuantity] = useState('');
  const [price, setPrice] = useState('');
  const [name, setName] = useState('');

  const handleAssemblyChange = (event: SelectChangeEvent) => {
    const assembly = assemblies?.find((a) => a.id === Number(event.target.value));
    setSelectedAssembly(assembly?.id.toString() || '');
    setName(assembly?.name || '');
    onRefresh();
  };

  useEffect(() => {
    if (stockPieces) {
      setSelectedPieces(stockPieces);
    }
  }, [stockPieces]);

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();

    const customPieces = selectedPieces.filter((piece) => piece.piece);
    const customSteles = selectedPieces.filter((piece) => piece.stele);
    const customPlatings = selectedPieces.filter((piece) => piece.plating);
    const customSoles = selectedPieces.filter((piece) => piece.sole);

    handleMutation(
      (data) => addStock(data),
      {
        name,
        quantity: Number.parseInt(quantity, 10),
        price: Number.parseFloat(price),
        isAssembly: true,
        assemblyId: Number.parseInt(selectedAssembly, 10),
        stockPieces: customPieces.map((piece) => ({
          pieceId: getPieceId(piece)!,
          granitId: piece.granit.id,
          newGeometry: piece.newGeometry || [],
        })),
        stockSteles: customSteles.map((piece) => {
          const { id, ...stele } = piece.stele!;
          return {
            ...stele,
            granitId: piece.granit.id,
          };
        }),
        stockPlatings: customPlatings.map((piece) => {
          const { id, isActive, ...plating } = piece.plating!;
          return {
            ...plating,
            granitId: piece.granit.id,
          };
        }),
        stockSoles: customSoles.map((piece) => {
          const { id, isActive, ...sole } = piece.sole!;
          return {
            ...sole,
            granitId: piece.granit.id,
          };
        }),
      },
      () => {
        toast.success('Stock ajouté avec succès');
        onRefresh();
      },
    );
  };

  const getPieceId = (piece: PieceInAssembly) => {
    if (piece.piece) return piece.piece.id;
    if (piece.stele) return piece.stele.id;
    if (piece.plating) return piece.plating.id;
    if (piece.sole) return piece.sole.id;
    return null;
  };

  return (
    <Container>
      <Form onSubmit={handleSubmit}>
        <FormControl fullWidth>
          <InputLabel id="assembly-label">Choisir un assemblage</InputLabel>
          <Select
            labelId="assembly-label"
            id="assembly-select"
            value={selectedAssembly}
            label="Assemblage"
            onChange={handleAssemblyChange}
            required
          >
            {assemblies?.map((assembly) => {
              let image = 'defaultImage';

              // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
              if (assembly.customImage) image = assembly.customImage;
              // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
              else if (!assembly.customImage && assembly.pictureUrl) image = assembly.pictureUrl;

              return (
                <MenuItem
                  key={assembly.id}
                  value={assembly.id.toString()}
                  sx={{ display: 'flex', alignItems: 'center', gap: 2, flexDirection: 'row' }}
                >
                  {assembly.pictureUrl && (
                    <Box
                      component="img"
                      src={`${buckets?.BUCKET_3D_PIECES || ''}/${image}`}
                      alt={assembly.name}
                      sx={{ width: 50, height: 50, objectFit: 'cover' }}
                    />
                  )}
                  <Typography variant="body1">{assembly.name}</Typography>
                </MenuItem>
              );
            })}
          </Select>
        </FormControl>

        {selectedPieces && selectedPieces.length > 0 && !isLoadingStockPieces ? (
          <div>
            <Typography variant="subtitle1" gutterBottom>
              Pièces de l&apos;assemblage
            </Typography>
            {selectedPieces.map((piece) => {
              const handleChangeGranit = (e: SelectChangeEvent) => {
                setSelectedPieces(
                  selectedPieces.map((p) => {
                    if (getPieceId(p) === getPieceId(piece)) {
                      const granit = granits?.find((g) => g.id === e.target.value);
                      return {
                        ...p,
                        granit: {
                          id: e.target.value,
                          name: granit?.name || '',
                          fabrication: granit?.fabrication || 'FRENCH',
                        },
                      };
                    }
                    return p;
                  }),
                );
              };

              return (
                <div
                  key={getPieceId(piece)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 2,
                    marginBottom: '24px',
                  }}
                >
                  <Row>
                    {piece?.piece?.pictureUrl && (
                      <Box
                        component="img"
                        src={`${buckets?.BUCKET_3D_PIECES || ''}/${piece.piece.pictureUrl}`}
                        alt={piece.piece.name}
                        sx={{ width: 50, height: 50, objectFit: 'cover' }}
                      />
                    )}

                    {piece.piece ? (
                      <Typography variant="body1">{piece?.piece?.name}</Typography>
                    ) : null}
                    {piece.stele ? <Typography variant="body1">Stèle custom</Typography> : null}
                    {piece.plating ? (
                      <Typography variant="body1">Placage custom {piece.plating.key}</Typography>
                    ) : null}
                    {piece.sole ? (
                      <Typography variant="body1">Semelle custom {piece.sole.key}</Typography>
                    ) : null}
                  </Row>
                  <FormControl sx={{ minWidth: 200 }}>
                    <InputLabel id={`granit-label-${getPieceId(piece)}`}>Granit</InputLabel>
                    <Select
                      sx={{ width: '300px' }}
                      labelId={`granit-label-${getPieceId(piece)}`}
                      value={piece.granit.id}
                      onChange={handleChangeGranit}
                      label="Granit"
                      required
                      renderValue={(value) => {
                        const granit = granits?.find((g) => g.id === value);
                        return granit?.name;
                      }}
                    >
                      {granits
                        ?.filter((g) => {
                          if (piece?.piece?.pieceFabrication) {
                            const fabricationPiece = piece?.piece?.pieceFabrication.find(
                              (p) => p.fabrication === g.fabrication,
                            );
                            return !!fabricationPiece;
                          }
                          return g.fabrication === 'FRENCH';
                        })
                        .map((granit) => (
                          <MenuItem
                            key={granit.id}
                            value={granit.id}
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 2,
                              flexDirection: 'row',
                            }}
                          >
                            {granit.image && (
                              <Box
                                component="img"
                                src={`${buckets?.BUCKET_3D_PIECES || ''}/${granit.image}`}
                                alt={granit.name}
                                sx={{ width: 50, height: 50, objectFit: 'cover' }}
                              />
                            )}
                            <Typography variant="body1">{granit.name}</Typography>
                          </MenuItem>
                        ))}
                    </Select>
                  </FormControl>
                </div>
              );
            })}
          </div>
        ) : null}

        <TextField
          fullWidth
          label="Quantité"
          type="number"
          value={quantity}
          onChange={(e) => setQuantity(e.target.value)}
          required
          inputProps={{ min: 1 }}
        />

        <TextField
          fullWidth
          label="Prix"
          type="number"
          value={price}
          onChange={(e) => setPrice(e.target.value)}
          required
          inputProps={{ min: 0, step: 1 }}
        />

        <TextField
          fullWidth
          label="Nom"
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
        />

        <Button type="submit" variant="contained" color="primary" fullWidth>
          Ajouter au stock
        </Button>
      </Form>
    </Container>
  );
};

const Container = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: 16,
});

const Form = styled('form')({
  display: 'flex',
  flexDirection: 'column',
  gap: 16,
});

const Row = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: 16,
});
